import MvrPreviewModal from './MvrPreviewModal';
import { MvrFlow, getMvrOrderStatus } from '../../services/mvrOrderApi';
import React, { useEffect, useMemo, useState } from 'react';
import { AvailableMvr, ingestMvr, listAvailableMvr } from '../../services/mvrFetchApi';

// "Import existing MVR" modal: pick a driver → list their MVR documents from DataFabric
// (each tagged with its type) → import one directly into the Data Room. No
// manual download/upload; the import runs the standard MVR pipeline server-side.

interface DriverLike {
  name?: string;
  firstName?: string;
  lastName?: string;
  licenseNumber?: string;
}

interface FetchMvrModalProps {
  quoteId: string;
  drivers: DriverLike[];
  onClose: () => void;
  onImported: (driverName: string) => void;   // refreshes the Data Room and confirms to the agent
}

function driverLabel(d: DriverLike): string {
  return d.name || [d.firstName, d.lastName].filter(Boolean).join(' ') || d.licenseNumber || 'Driver';
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(12,12,12,0.45)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const panel: React.CSSProperties = {
  background: '#fff', borderRadius: 14, width: 'min(640px, 100%)', maxHeight: '85vh',
  display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
};

const FetchMvrModal: React.FC<FetchMvrModalProps> = ({ quoteId, drivers, onClose, onImported }) => {
  const [selectedIdx, setSelectedIdx] = useState<number>(drivers.length ? 0 : -1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docs, setDocs] = useState<AvailableMvr[] | null>(null);
  const [previewFor, setPreviewFor] = useState<AvailableMvr | null>(null);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // The driver is already chosen, so the list loads itself rather than making
  // the agent press a button to see what is there.
  useEffect(() => {
    if (selectedDriver && quoteId) search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, selectedIdx]);

  /** Reload the list without clearing it, so statuses update in place. */
  const refresh = async () => {
    setRefreshing(true);
    try {
      await search(true);
    } finally {
      setRefreshing(false);
    }
  };


  /** Ask the vendor where a pending order got to, then reload the list. */
  const checkStatus = async (doc: AvailableMvr) => {
    if (!doc.orderId || !doc.flow) return;
    setCheckingId(doc.transactionId);
    try {
      await getMvrOrderStatus(quoteId, doc.flow as MvrFlow, doc.orderId, doc.transactionId);
      await search(true);
    } catch {
      await search(true);   // a failed poll is not fatal; the reload is the signal
    } finally {
      setCheckingId(null);
    }
  };

  const [ingestingId, setIngestingId] = useState<string | null>(null);
  const [importedIds, setImportedIds] = useState<Set<string>>(new Set());

  const selectedDriver = useMemo(
    () => (selectedIdx >= 0 ? drivers[selectedIdx] : undefined),
    [drivers, selectedIdx],
  );

  const search = async (silent = false) => {
    if (!selectedDriver || !quoteId) return;
    if (!silent) {
      setLoading(true);
      setDocs(null);
    }
    setError(null);
    try {
      const [firstName, ...rest] = (selectedDriver.name || '').trim().split(/\s+/);
      const result = await listAvailableMvr(quoteId, {
        licenseNumber: selectedDriver.licenseNumber,
        firstName: selectedDriver.firstName || firstName,
        lastName: selectedDriver.lastName || (rest.length ? rest.join(' ') : undefined),
      });
      setDocs(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch MVR documents.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const importDocument = async (doc: AvailableMvr) => {
    if (!quoteId) return;
    setIngestingId(doc.transactionId);
    setError(null);
    try {
      const name = doc.driverName || driverLabel(selectedDriver || {});
      await ingestMvr(quoteId, doc.transactionId, name);
      setImportedIds((prev) => new Set(prev).add(doc.transactionId));
      onImported(name);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import this MVR.');
    } finally {
      setIngestingId(null);
    }
  };

  return (
    <>
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Import existing MVR">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #eef2f0' }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0c0c0c' }}>Import existing MVR</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={refresh}
              disabled={!docs || refreshing || loading}
              title="Reload the list with the latest statuses"
              className="mvrl-btn mvrl-btn--ghost"
            >
              {refreshing ? <><span className="mvrp-spinner mvrp-spinner--dark" /> Refreshing…</> : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-2.64-6.36" /><polyline points="21 3 21 9 15 9" />
                  </svg>
                  Refresh
                </>
              )}
            </button>
            <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, color: '#64748b', lineHeight: 1 }}>×</button>
          </div>
        </div>

        {/* Driver picker */}
        <div style={{ padding: '14px 18px 10px' }}>
          <label style={{ display: 'block' }}>
            <span className="mvrl-label">Driver</span>
            <select
              className="mvrl-select"
              value={selectedIdx}
              disabled={loading}
              onChange={(e) => setSelectedIdx(Number(e.target.value))}
            >
              {drivers.length === 0 && <option value={-1}>No drivers on this quote</option>}
              {drivers.map((d, i) => (
                <option key={i} value={i}>
                  {driverLabel(d)}{d.licenseNumber ? ` — ${d.licenseNumber}` : ''}
                </option>
              ))}
            </select>
          </label>

        </div>

        {/* Results */}
        <div style={{ padding: '0 18px 16px', overflowY: 'auto' }}>
          {error && <div style={{ color: '#dc2626', fontSize: '0.82rem', padding: '8px 0' }}>⚠️ {error}</div>}

          {docs !== null && docs.length === 0 && !loading && (
            <div style={{ color: '#64748b', fontSize: '0.85rem', padding: '16px 0', textAlign: 'center' }}>
              No MVR documents found for this driver yet. If you have just
              ordered one, it may still be processing.
            </div>
          )}

          {docs && docs.map((doc) => {
            const imported = doc.imported || importedIds.has(doc.transactionId);
            const importable = doc.importable !== false;
            const tone = importable ? 'ok' : doc.pending ? 'wait' : 'err';
            return (
              <div key={doc.transactionId} className="mvrl-row">
                <div className="mvrl-main">
                  <div className="mvrl-tags">
                    <span className="mvrl-type">{doc.type}</span>
                    {doc.status && (
                      <span className={`mvrl-status mvrl-status--${tone}`}>
                        <span className="mvrl-status__dot" />
                        {doc.status}
                      </span>
                    )}
                  </div>
                  <div className="mvrl-driver">{doc.driverName || 'Driver'}</div>
                  <div className="mvrl-meta">
                    {doc.licenseNumber ? `${doc.licenseNumber} · ` : ''}
                    {doc.created ? new Date(doc.created).toLocaleDateString() : ''}
                  </div>
                </div>

                <div className="mvrl-actions">
                  <button
                    onClick={() => setPreviewFor(doc)}
                    disabled={!importable}
                    title={
                      doc.pending ? 'Nothing to view until the order completes'
                        : !importable ? 'This order produced no record'
                        : 'Open the full record'
                    }
                    className="mvrl-btn mvrl-btn--ghost"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                    </svg>
                    View record
                  </button>

                  {doc.refreshable && doc.orderId ? (
                    <button
                      onClick={() => checkStatus(doc)}
                      disabled={checkingId !== null}
                      title="Ask the vendor whether this order has completed"
                      className="mvrl-btn mvrl-btn--secondary"
                    >
                      {checkingId === doc.transactionId ? (
                        <><span className="mvrp-spinner mvrp-spinner--dark" /> Checking…</>
                      ) : (
                        <>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 12a9 9 0 1 1-2.64-6.36" /><polyline points="21 3 21 9 15 9" />
                          </svg>
                          Check status
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => importDocument(doc)}
                      disabled={ingestingId !== null || imported || !importable}
                      title={!importable ? 'This order produced no document' : 'Add this MVR to the quote'}
                      className={`mvrl-btn ${imported ? 'mvrl-btn--done' : 'mvrl-btn--primary'}`}
                    >
                      {!importable ? 'Unavailable'
                        : imported ? (
                          <>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Added
                          </>
                        ) : ingestingId === doc.transactionId ? (
                          <><span className="mvrp-spinner" /> Adding…</>
                        ) : (
                          <>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 5v14" /><path d="M5 12h14" />
                            </svg>
                            Add to quote
                          </>
                        )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>

    {previewFor && (
      <MvrPreviewModal
        quoteId={quoteId}
        transactionId={previewFor.transactionId}
        driverName={previewFor.driverName ?? undefined}
        onClose={() => setPreviewFor(null)}
      />
    )}
    </>
  );
};

export default FetchMvrModal;
